import React, { FormEvent, useEffect } from 'react';

import { FilterContext } from '../../../Context/FilterContext';
import Button from '../../../Form/Button/Button';
import AgeGroupComponent from './Components/AgeGroupComponent';
import SkinAppearanceComponent from './Components/SkinAppearanceComponent';
import { filterAgeGroupFields, filterSkinAppearanceFields } from './FieldFilter';
import { FilterField, SkinAppearancesFieldInterface } from './Types';

import './StandardFilter.scss';

interface Props {
    toggleVisibility: () => void;
    isVisible?: boolean;
    resetTimestamp: number;
}

interface StandardFilterFields {
    ageGroups: FilterField[];
    skinAppearances: SkinAppearancesFieldInterface[];
}

declare global {
    interface Window {
        __GGStandardFilterFields: StandardFilterFields;
        __GGOnlineAnalysisInfoText: string;
        __GGPhoneAnalysisInfoText: string;
        __GGConsultantAnalysisInfoText: string;
        __GGOnlineAnalysisIntroText: string;
        __GGPhoneAnalysisIntroText: string;
        __GGConsultantAnalysisIntroText: string;
        __GGPhoneAnalysisInfoPage: string;
    }
}

const StandardFilter: React.FunctionComponent<Props> = (props) => {
    const { standardFilterValues, updateStandardFilterValues } = React.useContext(FilterContext);

    // Read fields from global variable
    const fields = window.__GGStandardFilterFields;
    const [ageGroups, updateAgeGroups] = React.useState(fields.ageGroups);
    const [skinAppearances, updateSkinAppearances] = React.useState(fields.skinAppearances);

    // reset local state if filters are reset
    useEffect(() => {
        updateAgeGroups(fields.ageGroups);
        updateSkinAppearances(fields.skinAppearances);
    }, [standardFilterValues, props.resetTimestamp]);

    const submit = (event: FormEvent) => {
        event.preventDefault();

        const ageField = ageGroups.find((field) => field.checked);
        const skinAppearanceField = skinAppearances.find((field) => field.checked);
        updateStandardFilterValues({
            age: ageField ? ageField.filterValue : null,
            skinAppearance: skinAppearanceField ? skinAppearanceField.filterValue : null,
        });
        props.toggleVisibility();
    };

    const changeAge = (field: FilterField) => {
        const updatedAgeGroups = markFieldInGroup(field, ageGroups);
        updateAgeGroups(updatedAgeGroups);

        // trigger filter
        updateSkinAppearances(filterSkinAppearanceFields(updatedAgeGroups, skinAppearances));
    };

    const changeSkinAppearances = (field: SkinAppearancesFieldInterface) => {
        const updatedSkinAppearances = markFieldInGroup(field, skinAppearances);
        updateSkinAppearances(updatedSkinAppearances);

        // trigger filter
        updateAgeGroups(filterAgeGroupFields(updatedSkinAppearances, ageGroups));
    };

    // load initial values from context (local session)
    React.useEffect(() => {
        if (standardFilterValues) {
            const skinAppearancesFields = markFieldInGroup(
                getFieldForValue(standardFilterValues.skinAppearance, fields.skinAppearances),
                fields.skinAppearances,
            );
            const ageGroupFields = markFieldInGroup(
                getFieldForValue(standardFilterValues.age, fields.ageGroups),
                fields.ageGroups,
            );

            // update states with applied filter values
            updateAgeGroups(filterAgeGroupFields(skinAppearancesFields, ageGroupFields));
            updateSkinAppearances(filterSkinAppearanceFields(ageGroupFields, skinAppearancesFields));
        }
    }, [standardFilterValues]);

    if (props.isVisible === false) {
        return null;
    }

    return (
        <div className="standard-filter">
            <h3>
                <svg className="icon icon--controls">
                    <use xlinkHref="#icon-controls" />
                </svg>
                <span>Standard Filter</span>
            </h3>
            <form onSubmit={submit}>
                <fieldset className="standard-filter__agegroup">
                    <legend>Alter</legend>
                    <div className="standard-filter__skin-filter__agegroup-wrapper">
                        {ageGroups.map((field) => (
                            <AgeGroupComponent key={field.filterValue} onChange={changeAge} field={field} />
                        ))}
                    </div>
                </fieldset>

                <fieldset className="standard-filter__skin-appearence">
                    <legend>Hautbild</legend>
                    <div className="standard-filter__skin-appearence-wrapper">
                        {skinAppearances.map((field) => (
                            <SkinAppearanceComponent
                                key={field.filterValue}
                                onChange={changeSkinAppearances}
                                field={field}
                            />
                        ))}
                    </div>
                </fieldset>

                {ageGroups.some((field) => field.checked) ? (
                    <div className="standard-filter__button">
                        <Button primary={true}>Filter anwenden</Button>
                    </div>
                ) : null}
            </form>
        </div>
    );
};

const markFieldInGroup = <T extends FilterField>(
    field: T | undefined,
    fieldGroup: T[],
    allowMultiple: boolean = false,
): T[] => {
    if (!field) {
        return fieldGroup;
    }
    const updatedField = { ...field };

    // unmark all fields in group
    const newFieldGroup = fieldGroup.map((f) => {
        const newField = { ...f };
        if (allowMultiple === false) {
            newField.checked = false;
        }
        return newField;
    });

    updatedField.checked = !field.checked;

    // replace field in field group
    const index = newFieldGroup.findIndex((currentField) => currentField.filterValue === field.filterValue);
    // do not use `toSpliced` here to keep `LoggedOut` JS backwards compatible without pollyfills.
    // eslint-disable-next-line rulesdir/use-to-spliced-over-splice
    newFieldGroup.splice(index, 1, updatedField);

    return newFieldGroup;
};

const getFieldForValue = <T extends FilterField>(value: string | null, fieldGroup: T[]): T | undefined => {
    return fieldGroup.find((field) => field.filterValue === value);
};

export default StandardFilter;
